import React, { useMemo } from 'react';

import { css, cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { SmartLinkIconRenderer } from './SmartLinkIconRenderer.tsx';
import {
	type BylineConnectionIcon,
	getBylineConnectionIcons,
	MAX_BYLINE_CONNECTION_ICONS,
} from './utils/getBylineConnectionIcons.ts';
import { createPlaceholderConnectionIcon } from './utils/placeholderConnectionIcon.tsx';
import { getFallbackConnectionIcons } from './utils/fallbackConnectionIcons.tsx';
import type { EnhancedSmartLink } from './utils/smartLinkTypes.ts';

const styles = cssMap({
	stack: {
		display: 'inline-flex',
		alignItems: 'center',
		flexShrink: 0,
		isolation: 'isolate',
	},
	spacingBefore: {
		marginInlineStart: token('space.050'),
	},
	spacingAfter: {
		marginInlineEnd: token('space.050'),
	},
});

const appIconTileStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	boxSizing: 'border-box',
	position: 'relative',
	flexShrink: 0,
	width: '16px',
	height: '16px',
	overflow: 'hidden',
	backgroundColor: token('elevation.surface'),
	borderRadius: token('radius.tile'),
});

const glyphTileStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	boxSizing: 'border-box',
	position: 'relative',
	flexShrink: 0,
	width: '16px',
	height: '16px',
	backgroundColor: token('elevation.surface'),
	borderColor: token('color.border'),
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	borderRadius: token('radius.small'),
});

const firstIconTileStyles = css({
	zIndex: 300,
});

const secondIconTileStyles = css({
	marginInlineStart: token('space.negative.050'),
	zIndex: 200,
	rotate: '6deg',
});

const thirdIconTileStyles = css({
	marginInlineStart: token('space.negative.050'),
	zIndex: 100,
});

const isAtlasKitAppLogo = (icon: BylineConnectionIcon): boolean => icon.icon.kind === 'logo';

export type BylineConnectionIconStackProps = {
	links: EnhancedSmartLink[];
	incomingLinks?: EnhancedSmartLink[];
	relatedContentLinks?: EnhancedSmartLink[];
	additionalIcons?: BylineConnectionIcon[];
	appearance?: 'default' | 'placeholder';
	// Fill spare slots with AI tool icons, or link tiles while loading.
	minIcons?: number;
	// Keep the stack reusable before or after adjacent text, or let a parent own the spacing.
	spacing?: 'before' | 'after' | 'none';
};

export const BylineConnectionIconStack = ({
	links,
	incomingLinks,
	relatedContentLinks,
	additionalIcons,
	appearance = 'default',
	minIcons,
	spacing = 'after',
}: BylineConnectionIconStackProps): React.JSX.Element | null => {
	const realIcons = useMemo(
		() =>
			getBylineConnectionIcons({
				additionalIcons,
				relatedContentLinks,
				incomingLinks,
				outgoingLinks: links,
			}),
		[links, additionalIcons, incomingLinks, relatedContentLinks],
	);
	const icons = useMemo(() => {
		const target = Math.min(minIcons ?? 0, MAX_BYLINE_CONNECTION_ICONS);
		if (realIcons.length >= target) {
			return realIcons;
		}
		const padding =
			appearance === 'placeholder'
				? Array.from({ length: target - realIcons.length }, (_, index) =>
						createPlaceholderConnectionIcon(realIcons.length + index),
					)
				: getFallbackConnectionIcons(realIcons, target - realIcons.length);
		return [...realIcons, ...padding];
	}, [realIcons, minIcons, appearance]);

	if (icons.length === 0) {
		return null;
	}

	return (
		<span
			css={[
				styles.stack,
				spacing === 'before' && styles.spacingBefore,
				spacing === 'after' && styles.spacingAfter,
			]}
			aria-hidden="true"
			data-testid="byline-connections-icon-stack"
		>
			{icons.map((icon, index) => {
				const useAppChrome = isAtlasKitAppLogo(icon);
				const chip = useAppChrome ? 'app' : 'glyph';

				switch (index) {
					case 0:
						return (
							<span
								key={icon.key}
								css={[useAppChrome ? appIconTileStyles : glyphTileStyles, firstIconTileStyles]}
								data-chip={chip}
							>
								<SmartLinkIconRenderer icon={icon.icon} size="stack" />
							</span>
						);
					case 1:
						return (
							<span
								key={icon.key}
								css={[useAppChrome ? appIconTileStyles : glyphTileStyles, secondIconTileStyles]}
								data-chip={chip}
								data-rotation-enabled
							>
								<SmartLinkIconRenderer icon={icon.icon} size="stack" />
							</span>
						);
					case 2:
						return (
							<span
								key={icon.key}
								css={[useAppChrome ? appIconTileStyles : glyphTileStyles, thirdIconTileStyles]}
								data-chip={chip}
							>
								<SmartLinkIconRenderer icon={icon.icon} size="stack" />
							</span>
						);
					default:
						return null;
				}
			})}
		</span>
	);
};
